import { Loading } from '@chiroup/components/Loading';
import { LoadingPage } from '@chiroup/components/LoadingPage';
import { Maintenance } from '@chiroup/components/Maintenance';
import React, { lazy, Suspense, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 } from 'uuid';
import { InactivityLockProvider } from '../../contexts/inactivityLock.context';
import { MeContext } from '../../contexts/me.context';
import { UnreadMessagesProvider } from '../../contexts/unread-messages.context';
import WebsocketContext from '../../contexts/websocket.context';
import useMe from '../../hooks/useMe';
import LiveTranscriptionRoot from '../common/liveTranscription/LiveTranscriptionRoot';
import { shouldBlockToBilling } from './billingAccessGuards';
import BillingBanner from './BillingBanner';
import EhrGracePeriodBanner from './EhrGracePeriodBanner';

const NoClinicSelected = lazy(() => import('../NoClinicSelected'));
const ClinicSelectedContainer = lazy(() => import('./ClinicSelectedContainer'));
const PendingInvitesModal = lazy(() => import('../common/invites/PendingInvitesModal'));
const EHRTermsOfUseAcceptance = lazy(() => import('../EHRTermsOfUseAcceptance'));

type Props = {
  children: any;
};

const Container: React.FC<Props> = ({ children }) => {
  const sessionId = useMemo(() => v4(), []);
  const { pathname, state }: any = useLocation();
  const {
    me,
    isClinicSelectionResolved,
    updateMe,
    unselectClinic,
    selectClinic,
    selectLocation,
    hasAccess,
    hasEHR,
    hasVoiceToChartAccess,
    hasVoiceToChartSubscription,
    activeLocations,
    selectedLocationFull,
    hasRole,
    downForMaintenance,
    impersonatedFlags,
    toggleImpersonatedFlag,
    impersonatedRole,
    setRole,
    clearRole,
  } = useMe(state?.qsPlan);
  const navigate = useNavigate();
  // const [modalState, setModalState] = useState<OpenClosedStates>(
  //   OpenClosedStates.Closed
  // );

  useEffect(() => {
    if (me?.selectedClinic && shouldBlockToBilling(me.selectedClinic)) {
      navigate('/settings/subscription');
    }
  }, [me?.selectedClinic, navigate, pathname]);

  // TODO: FYI if we add this back, it is asking users who also have clinics that aren't education clinics to upload their student ID - it should only do this for education clinics
  // useEffect(() => {
  //   if (me) {
  //     if (me.studentRecord) {
  //       if (
  //         !me.studentRecord.studentId?.length &&
  //         dayjs().diff(dayjs(me?.createDate), 'hours') >= 48
  //       ) {
  //         if (pathname !== '/') {
  //           navigate('/');
  //         }
  //         setModalState(OpenClosedStates.Open);
  //       }
  //     }
  //   }
  // }, [history, me, pathname]);

  // const close = () => {
  //   getMe();
  //   setModalState(OpenClosedStates.Closed);
  // };

  return me && isClinicSelectionResolved ? (
    <Suspense fallback={<Loading />}>
      <MeContext.Provider
        value={{
          me,
          updateMe,
          unselectClinic,
          selectClinic,
          selectLocation,
          hasAccess,
          hasEHR,
          hasVoiceToChartAccess,
          hasVoiceToChartSubscription,
          activeLocations,
          selectedLocationFull,
          hasRole,
          sessionId,
          impersonatedFlags,
          toggleImpersonatedFlag,
          impersonatedRole,
          setRole,
          clearRole,
        }}
      >
        <InactivityLockProvider>
          <WebsocketContext>
            {!!me.pendingInvites?.length && (
              <Suspense fallback={null}>
                <PendingInvitesModal />
              </Suspense>
            )}
            {me.selectedClinic ? (
              <UnreadMessagesProvider>
                <ClinicSelectedContainer>
                  <EhrGracePeriodBanner />
                  <BillingBanner />
                  {hasEHR && !me.hasAcceptedEHRTerms && (
                    <Suspense fallback={null}>
                      <EHRTermsOfUseAcceptance isOpen />
                    </Suspense>
                  )}
                  <LiveTranscriptionRoot dictationEnabled>{children}</LiveTranscriptionRoot>
                </ClinicSelectedContainer>
              </UnreadMessagesProvider>
            ) : (
              <NoClinicSelected clinics={me.clinics} select={selectClinic} />
            )}
          </WebsocketContext>
        </InactivityLockProvider>
      </MeContext.Provider>
    </Suspense>
  ) : downForMaintenance ? (
    <Maintenance title={downForMaintenance.title} message={downForMaintenance.message} />
  ) : (
    <LoadingPage />
  );
};

export default Container;
